import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BizdevSalesAgentService } from './bizdev-sales-agent.service';

interface BizdevLookup {
    id?: number;
    category: string;
    label: string;
    value: string;
    sortOrder: number;
    active: boolean;
}

interface CategoryTab {
    title: string;
    category: string;
    rows: BizdevLookup[];
    loading: boolean;
    loaded: boolean;
    showForm: boolean;
    editing: BizdevLookup | null;
    formModel: BizdevLookup;
}

@Component({
    selector: 'app-bizdev-lookup-admin',
    templateUrl: './bizdev-lookup-admin.component.html',
    styleUrls: ['./bizdev-lookup-admin.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class BizdevLookupAdminComponent implements OnInit {

    private baseUrl = `${environment.apiUrl}/bizdev-lookups`;

    tabs: CategoryTab[] = [
        { title: 'Competitors',        category: 'COMPETITOR',         rows: [], loading: false, loaded: false, showForm: false, editing: null, formModel: this.emptyModel('COMPETITOR') },
        { title: 'Win Reasons',        category: 'WIN_REASON',         rows: [], loading: false, loaded: false, showForm: false, editing: null, formModel: this.emptyModel('WIN_REASON') },
        { title: 'Loss Reasons',       category: 'LOSS_REASON',        rows: [], loading: false, loaded: false, showForm: false, editing: null, formModel: this.emptyModel('LOSS_REASON') },
        { title: 'Lead Status',        category: 'LEAD_STATUS',        rows: [], loading: false, loaded: false, showForm: false, editing: null, formModel: this.emptyModel('LEAD_STATUS') },
        { title: 'Probability',        category: 'PROBABILITY',        rows: [], loading: false, loaded: false, showForm: false, editing: null, formModel: this.emptyModel('PROBABILITY') },
        { title: 'Ownership Interest', category: 'OWNERSHIP_INTEREST', rows: [], loading: false, loaded: false, showForm: false, editing: null, formModel: this.emptyModel('OWNERSHIP_INTEREST') },
    ];

    activeTabIndex = 0;

    // Sales Agents tab
    salesAgents: any[] = [];
    salesAgentsLoading = false;
    userSearchQuery = '';
    userSearchResults: any[] = [];
    userSearchLoading = false;

    private userSearchDebounce: any = null;

    constructor(
        private http: HttpClient,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private salesAgentService: BizdevSalesAgentService,
    ) {}

    ngOnInit(): void {
        this.loadTab(this.tabs[0]);
        this.loadSalesAgents();
    }

    loadSalesAgents(): void {
        this.salesAgentsLoading = true;
        this.salesAgentService.getSalesAgents().subscribe({
            next: (res: any) => {
                this.salesAgentsLoading = false;
                this.salesAgents = res?.data?.users || res?.data || [];
            },
            error: () => {
                this.salesAgentsLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load sales agents.' });
            }
        });
    }

    searchUsers(q: string): void {
        if (this.userSearchDebounce) { clearTimeout(this.userSearchDebounce); }
        if (!q || q.trim().length < 2) {
            this.userSearchResults = [];
            return;
        }
        this.userSearchDebounce = setTimeout(() => {
            this.userSearchLoading = true;
            this.salesAgentService.searchUsers(q.trim()).subscribe({
                next: (res: any) => {
                    this.userSearchLoading = false;
                    this.userSearchResults = res?.data?.users || res?.data || [];
                },
                error: () => {
                    this.userSearchLoading = false;
                    this.userSearchResults = [];
                }
            });
        }, 300);
    }

    addSalesAgent(user: any): void {
        this.salesAgentService.addSalesAgent(user.userId).subscribe({
            next: () => {
                this.salesAgents = [...this.salesAgents, user];
                this.userSearchQuery = '';
                this.userSearchResults = [];
                this.messageService.add({ severity: 'success', summary: 'Added', detail: `${user.firstName} ${user.lastName} added as sales agent.` });
            },
            error: (err: any) => {
                const msg = err?.error?.message || 'Could not add sales agent.';
                this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
            }
        });
    }

    removeSalesAgent(agent: any): void {
        this.salesAgentService.removeSalesAgent(agent.userId).subscribe({
            next: () => {
                this.salesAgents = this.salesAgents.filter(a => a.userId !== agent.userId);
                this.messageService.add({ severity: 'success', summary: 'Removed', detail: `${agent.firstName} ${agent.lastName} removed from sales agents.` });
            },
            error: (err: any) => {
                const msg = err?.error?.message || 'Could not remove sales agent.';
                this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
            }
        });
    }

    onTabChange(event: any): void {
        const tab = this.tabs[event.index];
        if (tab && !tab.loaded) {
            this.loadTab(tab);
        }
        // Index beyond tabs array = Sales Agents tab (static, no lazy-load needed)
    }

    private emptyModel(category: string): BizdevLookup {
        return { category, label: '', value: '', sortOrder: 0, active: true };
    }

    loadTab(tab: CategoryTab): void {
        tab.loading = true;
        this.http.get<any>(`${this.baseUrl}?category=${tab.category}`).subscribe({
            next: (res) => {
                tab.loading = false;
                tab.loaded = true;
                tab.rows = res?.lookups || res?.data?.lookups || [];
            },
            error: () => {
                tab.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: `Could not load ${tab.title}.` });
            }
        });
    }

    openAdd(tab: CategoryTab): void {
        tab.editing = null;
        tab.formModel = this.emptyModel(tab.category);
        tab.showForm = true;
    }

    openEdit(tab: CategoryTab, row: BizdevLookup): void {
        tab.editing = row;
        tab.formModel = { ...row };
        tab.showForm = true;
    }

    cancelForm(tab: CategoryTab): void {
        tab.showForm = false;
        tab.editing = null;
        tab.formModel = this.emptyModel(tab.category);
    }

    save(tab: CategoryTab): void {
        const model = tab.formModel;
        if (!model.label || !model.value) {
            this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Label and value are required.' });
            return;
        }
        const payload: BizdevLookup = {
            ...model,
            category: tab.category,
            sortOrder: Number(model.sortOrder) || 0,
            active: !!model.active,
        };
        this.http.post<any>(this.baseUrl, payload).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: `${tab.title} entry saved.` });
                tab.showForm = false;
                tab.editing = null;
                tab.formModel = this.emptyModel(tab.category);
                this.loadTab(tab);
            },
            error: (err) => {
                const msg = err?.error?.message || err?.statusText || 'Could not save entry.';
                this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
            }
        });
    }

    confirmDelete(tab: CategoryTab, row: BizdevLookup): void {
        this.confirmationService.confirm({
            message: `Delete "${row.label}"? This cannot be undone.`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.delete(tab, row),
        });
    }

    private delete(tab: CategoryTab, row: BizdevLookup): void {
        if (!row.id) { return; }
        this.http.delete<any>(`${this.baseUrl}/${row.id}`).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `${row.label} deleted.` });
                this.loadTab(tab);
            },
            error: (err) => {
                const msg = err?.error?.message || err?.statusText || 'Could not delete entry.';
                this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
            }
        });
    }
}
